'use client';

import { useState } from 'react';

import Link from 'next/link';

import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  CircleMinus,
  CreditCard,
  Download,
  Network,
  Package,
  Percent,
  RotateCcw,
  Scale,
  Target,
  TriangleAlert,
} from 'lucide-react';

import {
  DEFAULT_CURRENCY,
  formatMoney,
  getCurrencyName,
  getCurrencySymbol,
} from '@kit/shared/currency';
import { formatQuantity } from '@kit/shared/quantity';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';

import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';

import { CurrencyPicker } from '../_components/currency-picker';
import { RelatedTools } from '../_components/related-tools';
import {
  type ProfitMarginInputs,
  calculateProfitMargin,
  normalizeProfitMarginInput,
} from './_lib/calculate-profit-margin';

const baseUrl = appConfig.url;
const calculatorUrl = `${baseUrl}/tools/profit-margin-calculator`;
const toolsUrl = `${baseUrl}/tools`;

type FormData = Record<keyof ProfitMarginInputs, string>;

const DEFAULT_FORM_DATA: FormData = {
  monthlyUnits: '500',
  otherCost: '0.50',
  sellingPrice: '24.99',
  shippingCost: '1.20',
  targetMarginPercent: '35',
  transactionFeePercent: '2.9',
  unitCost: '8.40',
};

const faqItems = [
  {
    value: 'margin-vs-markup',
    question: 'What is the difference between margin and markup?',
    answer:
      'Margin is profit as a percentage of the selling price. Markup is profit as a percentage of the cost. A product that costs $25 and sells for $50 has a 50% margin and a 100% markup, the same $25 of profit described two different ways. Pricing to a "50% markup" when you meant a 50% margin leaves you well short.',
  },
  {
    value: 'how-to-calculate',
    question: 'How do you calculate profit margin?',
    answer:
      'Subtract cost from selling price to get profit, divide by the selling price, then multiply by 100. Gross margin uses only the unit cost. Net margin also subtracts shipping, payment fees, and overhead. The defaults: $24.99 minus $8.40 is $16.59 (66.4% gross). After $1.20 shipping, $0.72 fees, and $0.50 overhead, net profit is $14.17 (56.7%).',
  },
  {
    value: 'worked-example',
    question: 'What does the example on this page show?',
    answer:
      'An $8.40 landed unit at $24.99 looks like a 66.4% gross margin and a 197.5% markup. After $1.20 outbound shipping, 2.9% processing, and $0.50 overhead it nets $14.17 (56.7%). A 35% net target only needs $16.26. Break-even is $10.40. Pricing a 50% markup ($12.60) when you meant a 50% margin is how you underprice.',
  },
  {
    value: 'gross-vs-net',
    question: 'What is the difference between gross margin and net margin?',
    answer:
      'Gross margin counts only what the product itself cost you. Net margin also subtracts the costs of actually completing the sale: outbound shipping, payment processing fees, and allocated overhead. Net margin is the number that decides whether the business makes money.',
  },
  {
    value: 'good-margin',
    question: 'What is a good profit margin for ecommerce?',
    answer:
      'It depends on the channel and the price point. Many ecommerce sellers aim for a gross margin above 60% and a net margin between 15% and 30%, because advertising and fulfillment consume the difference. Rather than chase a benchmark, set a target margin in the calculator and check whether your price supports it.',
  },
  {
    value: 'target-price',
    question: 'How do I work out the price I need for a target margin?',
    answer:
      'Divide your fixed cost per unit by one minus the target margin, and minus your percentage fees. This calculator does it for you: set a target margin and it returns the selling price that achieves it. Note that you cannot simply add the target margin to your cost. That produces a markup, which lands below the margin you wanted.',
  },
  {
    value: 'break-even',
    question: 'What is break-even price?',
    answer:
      'The break-even price is the lowest price that still covers every cost, leaving exactly zero profit. Because payment processing takes a percentage of whatever the item sells for, break-even is higher than your cost per unit, because you must also cover the fee charged on the price itself.',
  },
  {
    value: 'landed-cost',
    question: 'What cost should I enter for the product?',
    answer:
      'Use landed cost, not the supplier invoice price. Landed cost includes freight, duty, and prep, and is routinely 30% to 60% above the ex-works price you negotiated. Entering the invoice price alone is the most common reason a margin calculation looks healthy and the bank balance does not.',
  },
  {
    value: 'free',
    question: 'Is this profit margin calculator free?',
    answer:
      'Yes. It runs entirely in your browser with no signup, no usage limit, and no credit card. You can export any scenario to CSV.',
  },
] as const;

const conceptCards = [
  {
    title: 'Gross margin',
    description:
      'Selling price minus the unit cost of the product, as a percentage of the price. Measures the product itself.',
    icon: Package,
  },
  {
    title: 'Net margin',
    description:
      'The same calculation after shipping, payment fees, and overhead. Measures the business.',
    icon: Percent,
  },
  {
    title: 'Markup',
    description:
      'Profit over cost rather than over price. Always a bigger number than margin, and easy to confuse with it.',
    icon: Scale,
  },
  {
    title: 'Target price',
    description:
      'Work backwards: name the margin you want and get the price that delivers it after fees.',
    icon: Target,
  },
] as const;

function parseInput(value: string) {
  const parsed = Number.parseFloat(value);

  return Number.isFinite(parsed) ? parsed : 0;
}

function formatPercent(value: number | null) {
  return value === null ? 'N/A' : `${value.toFixed(1)}%`;
}

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Free Profit Margin Calculator: Gross, Net, and Target Price',
    alternateName: 'Gross and Net Profit Margin Calculator',
    description:
      'Calculate gross margin, net margin, markup, break-even price, and the selling price that hits a target margin.',
    url: calculatorUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    creator: { '@type': 'Organization', name: appConfig.name, url: baseUrl },
    featureList: [
      'Calculate gross profit margin',
      'Calculate net profit margin',
      'Convert between margin and markup',
      'Calculate break-even selling price',
      'Reverse-price a product to a target margin',
      'Estimate monthly revenue and profit',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Free Tools', item: toolsUrl },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Profit Margin Calculator',
        item: calculatorUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

function CalculatorInput({
  help,
  id,
  label,
  onChange,
  prefix,
  prefixCurrency,
  step = '0.01',
  suffix,
  value,
}: {
  help?: string;
  id: string;
  label: string;
  onChange: (value: string) => void;
  prefix?: string;
  /** ISO code announced to screen readers, since several currencies share "$". */
  prefixCurrency?: string;
  step?: string;
  suffix?: string;
  value: string;
}) {
  const helpId = help ? `${id}-help` : undefined;
  const unitId = prefix || suffix ? `${id}-unit` : undefined;
  const describedBy = [unitId, helpId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-foreground text-sm font-bold">
        {label}
      </Label>
      <div className="relative">
        {prefix ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-sm"
          >
            {prefix}
          </span>
        ) : null}
        <Input
          id={id}
          aria-describedby={describedBy}
          type="number"
          min="0"
          max={suffix === '%' ? '100' : undefined}
          step={step}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`border-border bg-muted h-12 rounded-xl shadow-none ${
            prefix ? 'pl-8' : ''
          } ${suffix ? 'pr-9' : ''}`}
        />
        {suffix ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-sm"
          >
            {suffix}
          </span>
        ) : null}
      </div>
      {unitId ? (
        <span id={unitId} className="sr-only">
          {prefixCurrency
            ? `Amount in ${getCurrencyName(prefixCurrency)}.`
            : null}
          {suffix === '%' ? 'Percentage from 0 to 100.' : null}
        </span>
      ) : null}
      {help ? (
        <p id={helpId} className="text-muted-foreground text-xs leading-5">
          {help}
        </p>
      ) : null}
    </div>
  );
}

export default function ProfitMarginCalculatorPage() {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  const formatCurrency = (value: number) => formatMoney({ currency, value });
  const currencySymbol = getCurrencySymbol(currency);
  const startFreePath = pathsConfig.auth.startFree;

  const inputs: ProfitMarginInputs = {
    monthlyUnits: parseInput(formData.monthlyUnits),
    otherCost: parseInput(formData.otherCost),
    sellingPrice: parseInput(formData.sellingPrice),
    shippingCost: parseInput(formData.shippingCost),
    targetMarginPercent: parseInput(formData.targetMarginPercent),
    transactionFeePercent: parseInput(formData.transactionFeePercent),
    unitCost: parseInput(formData.unitCost),
  };

  const result = calculateProfitMargin(inputs);
  const isProfitable = result.netProfit > 0;
  const isLoss = result.netProfit < 0;
  const underpriced =
    result.targetPrice !== null && inputs.sellingPrice < result.targetPrice;

  function updateField(key: keyof FormData, value: string) {
    setFormData((current) => ({
      ...current,
      [key]: normalizeProfitMarginInput(key, value),
    }));
  }

  function resetExample() {
    setFormData(DEFAULT_FORM_DATA);
  }

  function downloadReport() {
    const rows = [
      ['Metric', 'Value'],
      ['Selling price', inputs.sellingPrice.toFixed(2)],
      ['Unit cost (landed)', inputs.unitCost.toFixed(2)],
      ['Outbound shipping per unit', inputs.shippingCost.toFixed(2)],
      ['Transaction fee percent', inputs.transactionFeePercent.toFixed(2)],
      ['Other cost per unit', inputs.otherCost.toFixed(2)],
      ['Units per month', inputs.monthlyUnits.toFixed(0)],
      ['Gross profit per unit', result.grossProfit.toFixed(2)],
      ['Gross margin percent', result.grossMarginPercent.toFixed(1)],
      ['Total cost per unit', result.totalCostPerUnit.toFixed(2)],
      ['Net profit per unit', result.netProfit.toFixed(2)],
      ['Net margin percent', result.netMarginPercent.toFixed(1)],
      [
        'Markup percent',
        result.markupPercent === null ? 'N/A' : result.markupPercent.toFixed(1),
      ],
      [
        'ROI percent',
        result.roiPercent === null ? 'N/A' : result.roiPercent.toFixed(1),
      ],
      [
        'Break-even price',
        result.breakEvenPrice === null
          ? 'N/A'
          : result.breakEvenPrice.toFixed(2),
      ],
      ['Target margin percent', inputs.targetMarginPercent.toFixed(1)],
      [
        'Price for target margin',
        result.targetPrice === null ? 'N/A' : result.targetPrice.toFixed(2),
      ],
      ['Monthly revenue', result.monthlyRevenue.toFixed(2)],
      ['Monthly net profit', result.monthlyNetProfit.toFixed(2)],
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `profit-margin-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const costRows = [
    { label: 'Unit cost (landed)', value: inputs.unitCost },
    { label: 'Outbound shipping', value: inputs.shippingCost },
    { label: 'Payment processing', value: result.transactionFee },
    { label: 'Other cost per unit', value: inputs.otherCost },
  ];

  return (
    <>
      <StructuredData />

      <div className="bg-background text-foreground min-h-screen">
        <section className="border-border relative overflow-hidden border-b">
          <div className="absolute inset-x-0 top-0 h-[480px] bg-[radial-gradient(circle_at_top_right,_color-mix(in_srgb,var(--primary)_calc(0.13*100%),transparent),_transparent_44%)]" />
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="max-w-2xl">
                <div className="bg-primary/10 text-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase">
                  <Percent className="h-4 w-4" />
                  Free pricing tool
                </div>

                <h1 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                  <span className="text-primary">Profit Margin Calculator</span>
                  : Gross, Net, and Target Price
                </h1>
                <p className="text-muted-foreground mt-6 max-w-xl text-lg leading-8">
                  Free tool for Shopify and ecommerce sellers. An $8.40 unit at
                  $24.99 is a 66.4% gross margin. After $1.20 shipping, 2.9%
                  fees, and $0.50 overhead it nets <strong>$14.17</strong>{' '}
                  (56.7%). A 35% net target only needs $16.26. Pricing a 50%
                  markup when you meant a 50% margin is how you underprice.
                </p>

                <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
                  {[
                    'Gross and net side by side',
                    'Reverse-price to a target margin',
                    'No signup required',
                  ].map((item) => (
                    <span
                      key={item}
                      className="text-muted-foreground inline-flex items-center gap-2 text-sm font-medium"
                    >
                      <CheckCircle2 className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ))}
                </div>

                <p className="text-muted-foreground mt-8 text-sm leading-6">
                  The calculator opens with a typical private-label example.
                  Replace every field with your own numbers, and use{' '}
                  <Link
                    href="/tools/landed-cost-calculator"
                    className="text-primary font-semibold hover:underline"
                  >
                    landed cost
                  </Link>{' '}
                  for the unit cost, not the supplier invoice price.
                </p>
              </div>

              <div
                aria-live="polite"
                className="bg-primary text-on-primary rounded-[10px] p-7 shadow-[0_24px_70px_-30px_rgba(38,31,28,0.7)] sm:p-9"
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="text-on-primary text-xs font-bold tracking-[0.18em] uppercase">
                      Net profit margin
                    </p>
                    {/* No colour shift for the loss state: every red dark
                        enough to read on this coral is illegible. The minus
                        sign and the icon swap carry the signal instead. */}
                    <p className="mt-3 text-5xl font-extrabold tracking-tight tabular-nums">
                      {formatPercent(result.netMarginPercent)}
                    </p>
                  </div>
                  <div className="bg-on-primary/10 rounded-2xl p-3">
                    {isProfitable ? (
                      <BarChart3 className="h-7 w-7" />
                    ) : isLoss ? (
                      <TriangleAlert className="h-7 w-7" />
                    ) : (
                      <CircleMinus className="h-7 w-7" />
                    )}
                  </div>
                </div>

                <div className="border-on-primary/20 mt-8 grid grid-cols-2 gap-4 border-t pt-7">
                  {[
                    ['Gross margin', formatPercent(result.grossMarginPercent)],
                    ['Markup', formatPercent(result.markupPercent)],
                    ['Net profit / unit', formatCurrency(result.netProfit)],
                    ['ROI on cost', formatPercent(result.roiPercent)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-on-primary/10 rounded-xl p-4"
                    >
                      <p className="text-[11px] font-bold tracking-[0.14em] uppercase opacity-80">
                        {label}
                      </p>
                      <p className="mt-1.5 text-xl font-extrabold tabular-nums">
                        {value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="border-on-primary/20 mt-7 border-t pt-6">
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="text-sm font-bold">
                      Price for a {inputs.targetMarginPercent.toFixed(0)}%
                      margin
                    </p>
                    <p className="text-2xl font-extrabold tabular-nums">
                      {result.targetPrice === null
                        ? 'N/A'
                        : formatCurrency(result.targetPrice)}
                    </p>
                  </div>
                  <p className="mt-2 text-[13px] leading-6 opacity-85">
                    {result.targetPrice === null
                      ? 'That margin is unreachable at these fees. The percentage costs leave nothing behind.'
                      : underpriced
                        ? `You are pricing ${formatCurrency(result.targetPrice - inputs.sellingPrice)} below the price that target needs.`
                        : 'Your current price already clears that target.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-border bg-background border-b px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="border-border bg-card rounded-[10px] border p-6 shadow-sm sm:p-10">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">
                    Calculate your margin
                  </h2>
                  <p className="text-muted-foreground mt-2 max-w-xl text-sm leading-6">
                    Every figure is per unit unless labelled otherwise. Results
                    update as you type.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetExample}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset example
                  </Button>
                  <Button type="button" onClick={downloadReport}>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>
              </div>

              <form
                onSubmit={(event) => event.preventDefault()}
                className="mt-8 grid gap-x-6 gap-y-6 md:grid-cols-2 lg:grid-cols-3"
              >
                <CurrencyPicker
                  hint="Enter every figure in this currency."
                  onChange={setCurrency}
                  value={currency}
                />

                <CalculatorInput
                  id="sellingPrice"
                  label="Selling price"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="What the customer pays"
                  value={formData.sellingPrice}
                  onChange={(value) => updateField('sellingPrice', value)}
                />
                <CalculatorInput
                  id="unitCost"
                  label="Unit cost (landed)"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="Product cost including freight, duty and prep"
                  value={formData.unitCost}
                  onChange={(value) => updateField('unitCost', value)}
                />
                <CalculatorInput
                  id="shippingCost"
                  label="Outbound shipping per unit"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="What it costs to get one unit to the customer"
                  value={formData.shippingCost}
                  onChange={(value) => updateField('shippingCost', value)}
                />
                <CalculatorInput
                  id="transactionFeePercent"
                  label="Payment processing fee"
                  suffix="%"
                  help="Card or marketplace fee, often around 2.9%"
                  value={formData.transactionFeePercent}
                  onChange={(value) =>
                    updateField('transactionFeePercent', value)
                  }
                />
                <CalculatorInput
                  id="otherCost"
                  label="Other cost per unit"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="Software, packaging, allocated overhead"
                  value={formData.otherCost}
                  onChange={(value) => updateField('otherCost', value)}
                />
                <CalculatorInput
                  id="targetMarginPercent"
                  label="Target net margin"
                  suffix="%"
                  help="The margin you want, and we return the price for it"
                  value={formData.targetMarginPercent}
                  onChange={(value) =>
                    updateField('targetMarginPercent', value)
                  }
                />
                <CalculatorInput
                  id="monthlyUnits"
                  label="Units per month"
                  step="1"
                  value={formData.monthlyUnits}
                  onChange={(value) => updateField('monthlyUnits', value)}
                />
              </form>

              <div className="mt-10 grid gap-6 lg:grid-cols-2">
                <div className="border-border bg-muted rounded-2xl border p-6">
                  <p className="text-muted-foreground text-xs font-bold tracking-[0.16em] uppercase">
                    Where the price goes
                  </p>
                  <div className="mt-5 space-y-3">
                    {costRows.map((row) => (
                      <div
                        key={row.label}
                        className="flex items-baseline justify-between gap-4 text-sm"
                      >
                        <span className="text-muted-foreground">
                          {row.label}
                        </span>
                        <span className="font-bold tabular-nums">
                          {formatCurrency(row.value)}
                        </span>
                      </div>
                    ))}
                    <div className="border-border flex items-baseline justify-between gap-4 border-t pt-3 text-sm">
                      <span className="font-bold">Total cost per unit</span>
                      <span className="font-extrabold tabular-nums">
                        {formatCurrency(result.totalCostPerUnit)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-4 text-sm">
                      <span className="font-bold">Net profit per unit</span>
                      <span className="text-primary font-extrabold tabular-nums">
                        {formatCurrency(result.netProfit)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-border bg-muted rounded-2xl border p-6">
                  <p className="text-muted-foreground text-xs font-bold tracking-[0.16em] uppercase">
                    Monthly at {formatQuantity(inputs.monthlyUnits)} units
                  </p>
                  <div className="mt-5 space-y-3">
                    <div className="flex items-baseline justify-between gap-4 text-sm">
                      <span className="text-muted-foreground">Revenue</span>
                      <span className="font-bold tabular-nums">
                        {formatCurrency(result.monthlyRevenue)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-4 text-sm">
                      <span className="text-muted-foreground">
                        Gross profit
                      </span>
                      <span className="font-bold tabular-nums">
                        {formatCurrency(result.monthlyGrossProfit)}
                      </span>
                    </div>
                    <div className="border-border flex items-baseline justify-between gap-4 border-t pt-3 text-sm">
                      <span className="font-bold">Net profit</span>
                      <span className="text-primary font-extrabold tabular-nums">
                        {formatCurrency(result.monthlyNetProfit)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-4 text-sm">
                      <span className="text-muted-foreground">
                        Break-even price
                      </span>
                      <span className="font-bold tabular-nums">
                        {result.breakEvenPrice === null
                          ? 'N/A'
                          : formatCurrency(result.breakEvenPrice)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {isLoss ? (
                <div className="border-warn-fg/25 bg-warn-bg text-warn-fg mt-6 flex gap-3 rounded-2xl border p-5 text-sm leading-6">
                  <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>
                    At this price the product loses{' '}
                    {formatCurrency(Math.abs(result.netProfit))} per unit. It
                    needs to sell for at least{' '}
                    {result.breakEvenPrice === null
                      ? 'more than the fees allow'
                      : formatCurrency(result.breakEvenPrice)}{' '}
                    to break even.
                  </span>
                </div>
              ) : null}

              <div className="border-border mt-8 flex flex-col gap-5 border-t pt-8 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 text-primary hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:flex">
                    <Network className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-base font-bold">
                      This margin is only as good as your unit cost.
                    </p>
                    <p className="text-muted-foreground mt-1 max-w-xl text-sm leading-6">
                      {appConfig.name} tracks the real cost of every purchase
                      order, including supplier price, freight, duty, and prep,
                      so the margin you model here matches the one you actually
                      earn.
                    </p>
                  </div>
                </div>
                <Button
                  asChild
                  className="h-11 shrink-0 rounded-xl px-6 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card border-border border-b px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
              The four numbers
            </p>
            <h2 className="font-heading mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
              Margin, markup, and the gap between them
            </h2>
            <p className="text-muted-foreground mt-5 max-w-3xl text-base leading-8">
              Margin and markup describe the same profit against different
              denominators, and mixing them up is the most expensive arithmetic
              error in ecommerce pricing. A product costing{' '}
              {formatCurrency(inputs.unitCost)} sold at{' '}
              {formatCurrency(inputs.sellingPrice)} carries a{' '}
              <strong>{formatPercent(result.grossMarginPercent)}</strong> gross
              margin but a{' '}
              <strong>{formatPercent(result.markupPercent)}</strong> markup.
              Price to the markup figure when you meant the margin and you will
              land well under the number you planned for.
            </p>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {conceptCards.map(({ title, description, icon: Icon }) => (
                <div
                  key={title}
                  className="border-border bg-background rounded-2xl border p-6"
                >
                  <span className="bg-accent-tint text-accent-deep flex size-11 items-center justify-center rounded-xl">
                    <Icon className="size-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{title}</h3>
                  <p className="text-muted-foreground mt-2 text-sm leading-6">
                    {description}
                  </p>
                </div>
              ))}
            </div>

            <div className="border-border bg-muted mt-10 rounded-2xl border p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 text-primary hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:flex">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-base font-bold">
                    Selling on Amazon or a Shopify store?
                  </p>
                  <p className="text-muted-foreground mt-2 text-sm leading-7">
                    Referral fees, fulfilment fees, and ad spend change the
                    picture enough to deserve their own tools. Use the{' '}
                    <Link
                      href="/tools/amazon-profit-calculator"
                      className="text-primary font-semibold hover:underline"
                    >
                      Amazon profit calculator
                    </Link>{' '}
                    for FBA, or the{' '}
                    <Link
                      href="/tools/dropshipping-profit-calculator"
                      className="text-primary font-semibold hover:underline"
                    >
                      dropshipping profit calculator
                    </Link>{' '}
                    for a DTC store. To get the unit cost right in the first
                    place, start with the{' '}
                    <Link
                      href="/tools/landed-cost-calculator"
                      className="text-primary font-semibold hover:underline"
                    >
                      landed cost calculator
                    </Link>{' '}
                    or the{' '}
                    <Link
                      href="/tools/import-duty-calculator"
                      className="text-primary font-semibold hover:underline"
                    >
                      import duty calculator
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-heading text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              Profit Margin Calculator FAQ
            </h2>
            <div className="mt-10">
              <Accordion
                type="single"
                collapsible
                defaultValue="margin-vs-markup"
              >
                {faqItems.map((item) => (
                  <AccordionItem
                    key={item.value}
                    value={item.value}
                    className="border-border bg-card mb-4 overflow-hidden rounded-2xl border px-6"
                  >
                    <AccordionTrigger className="py-6 text-left text-base font-bold hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section className="bg-foreground text-background relative overflow-hidden py-24 sm:py-32">
          <div className="bg-primary/20 absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
            <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
              The calculator is free. The margin is the hard part.
            </p>
            <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl">
              A margin built on a guessed cost is a guess.
            </h2>
            <p className="text-background/70 mt-6 text-lg leading-8">
              Every number above depends on what your unit truly costs
              delivered. {appConfig.name} reads your invoices, files them
              against the right order, and keeps that cost current as freight
              and duty land, so the margin you priced against is the margin you
              get.
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
              <Button
                asChild
                className="bg-background text-foreground hover:bg-background/90 h-12 rounded-xl px-7 font-bold"
              >
                <Link href={startFreePath}>
                  Start Your 14-Day Trial
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="text-background hover:bg-background/10 h-12 rounded-xl border-white/25 bg-transparent px-7 font-bold"
              >
                <Link href="/#features">See how it works</Link>
              </Button>
            </div>

            <div className="text-background/60 mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                (item) => (
                  <span key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="text-primary h-4 w-4" />
                    {item}
                  </span>
                ),
              )}
            </div>
          </div>
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl">
            <h2 className="text-foreground text-2xl font-bold">
              Related reading
            </h2>
            <ul className="text-muted-foreground mt-4 space-y-3 text-sm leading-7">
              <li>
                <Link
                  href="/blog/how-to-calculate-landed-cost"
                  className="text-primary font-semibold hover:underline"
                >
                  How to calculate landed cost
                </Link>
                : the inbound unit this calculator should be using, not the
                factory invoice.
              </li>
              <li>
                <Link
                  href="/blog/landed-cost-vs-cogs"
                  className="text-primary font-semibold hover:underline"
                >
                  Landed cost vs COGS
                </Link>
                : why a $10 invoice is not the cost that hits the P&amp;L.
              </li>
              <li>
                <Link
                  href="/blog/the-true-cost-of-a-product-factory-to-warehouse"
                  className="text-primary font-semibold hover:underline"
                >
                  True product cost, factory to warehouse
                </Link>
                : a $4.80 factory SKU that is $6.20 once it is sellable stock.
              </li>
              <li>
                <Link
                  href="/tools/amazon-profit-calculator"
                  className="text-primary font-semibold hover:underline"
                >
                  Amazon profit calculator
                </Link>{' '}
                and the{' '}
                <Link
                  href="/tools/dropshipping-profit-calculator"
                  className="text-primary font-semibold hover:underline"
                >
                  dropshipping profit calculator
                </Link>
                : channel fees and ads on top of the margin you just priced.
              </li>
            </ul>
          </div>
          <RelatedTools currentPath="/tools/profit-margin-calculator" />
        </section>
      </div>
    </>
  );
}
